import {
  HEIGHT_UNIT,
  formatHeight,
  fromFeetAndInches,
} from '@wlb/shared/utils/heightUtils';

export const DEFAULT_HEIGHT_METRES = 1.7272;
export const DEFAULT_HEIGHT_UNIT = HEIGHT_UNIT.FEET;

export const heightScaleValue = (metres: number, unit: string): number =>
  unit === HEIGHT_UNIT.FEET
    ? Math.round((metres * 100) / 2.54)
    : Number(metres.toFixed(2));

export const heightMetresFromScale = (value: number, unit: string): number => {
  if (unit !== HEIGHT_UNIT.FEET) return Number(value.toFixed(2));

  const feet = Math.floor(value / 12);
  const inches = value % 12;
  return fromFeetAndInches(feet, inches);
};

export const formatOnboardingHeight = (
  metres: number,
  unit: string,
): string => formatHeight(
  metres,
  unit === HEIGHT_UNIT.FEET ? 'ft' : 'm',
);

const MAX_INCHES = 11;

/**
 * A lone number in feet mode is the `feet.inches` shorthand people type here:
 * `5.6` is 5 ft 6 in, not 5.6 ft (which would round to 5 ft 7 in). The decimals
 * are the inches, so `5.11` is 5 ft 11 in and anything past 11 is rejected.
 */
const FEET_SHORTHAND = /^(\d{1,2})(?:\.(\d{0,2}))?$/;

const parseFeetShorthand = (token: string): number | null => {
  const match = token.match(FEET_SHORTHAND);
  if (!match) return null;

  const feet = Number(match[1]);
  const inches = match[2] ? Number(match[2]) : 0;
  if (inches > MAX_INCHES) return null;
  if (feet <= 0 && inches <= 0) return null;

  return fromFeetAndInches(feet, inches);
};

export const parseOnboardingHeight = (
  input: string,
  unit: string,
): number | null => {
  const tokens = input.match(/\d+(?:\.\d*)?/g) ?? [];
  const values = tokens.map(Number);
  if (tokens.length === 0 || values.some(value => !Number.isFinite(value))) return null;

  if (unit !== HEIGHT_UNIT.FEET) {
    return tokens.length === 1 && values[0] > 0 ? values[0] : null;
  }

  if (tokens.length === 1) {
    return parseFeetShorthand(tokens[0]);
  }

  const [feet, inches] = values;
  if (
    !Number.isInteger(feet) ||
    !Number.isInteger(inches) ||
    feet < 0 ||
    inches < 0 ||
    inches > MAX_INCHES
  ) {
    return null;
  }

  return fromFeetAndInches(feet, inches);
};
